import InfoIcon from 'admin/icons/info.svg';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import {
  hideLoadingSpinner,
  showLoadingSpinner,
} from '~/javascripts/admin/actions/loading-spinner';
import {
  updateOrganizationApplicationTemplate,
  uploadOrganizationApplicationTermFile,
} from '~/javascripts/admin/actions/merchant-application-templates';
import { getUserTagGroups } from '~/javascripts/admin/actions/user-tag-group';
import { useAppDispatch, useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { ColorKey, GradientKey } from '~/javascripts/admin/theme/tokens/colors';
import { getFrontEnv } from '~/javascripts/admin/utils';
import { Button } from '../../atoms/Buttons';
import CopyToClipboard from '../../atoms/CopyToClipboard';
import FormErrorMessage from '../../atoms/FormErrorMessage';
import Grid from '../../atoms/Grid';
import Input from '../../atoms/Input';
import Label from '../../atoms/Label';
import NotificationToast from '../../molecules/NotificationToast';
import Radio from '../../atoms/Radio';
import Tooltip from '../../atoms/Tooltip';
import Required from '../../atoms/Required';
import Select from '../../atoms/Select';
import TextArea from '../../atoms/TextArea';
import TextBox from '../../atoms/TextBox';

type CreateResult = {
  id: string;
};

type Props = (
  | {
      mode: 'create';
      formValues?: undefined;
      templateId?: undefined;
    }
  | {
      mode: 'edit';
      formValues: MerchantApplicationTemplateValues;
      templateId: string;
    }
) & {
  onConfirm: (formValues: MerchantApplicationTemplateValues) => Promise<CreateResult | void>;
};

type UserTagGroupOption = {
  user_tag_group_id: string;
  field_option: 'required' | 'optional';
};

export type MerchantApplicationTemplateValues = {
  title: string;
  status: 'draft' | 'active' | 'archived';
  application_term_url: string;
  // 申込完了後にリダイレクトする先のURL（任意）。空文字なら未設定（リダイレクトしない）。
  redirect_url: string;
  private_money_ids: string[];
  capital_field_option: 'required' | 'optional' | 'hidden';
  number_of_employees_field_option: 'required' | 'optional' | 'hidden';
  referral_source_field_option: 'required' | 'optional' | 'hidden';
  description: string;
  user_tag_group_options?: UserTagGroupOption[];
  // Only available in edit mode
  id?: string;
  issuer_organization_code?: string;
};

const DEFAULT_FORM_VALUES: MerchantApplicationTemplateValues = {
  title: '',
  status: 'draft',
  application_term_url: '',
  redirect_url: '',
  private_money_ids: [],
  capital_field_option: 'required',
  number_of_employees_field_option: 'required',
  referral_source_field_option: 'optional',
  description: '',
};

const Form = styled.form`
  margin-top: 24px;
`;

const FormGrid = styled(Grid.Container)`
  ${Label} {
    margin-bottom: 0;
    padding: 5px 0;
    ${(props) => props.theme.typography.body2.regular}
  }

  ${Grid.Divider} {
    margin: 12px 0;
  }

  ${Select as any} {
    line-height: 1.5;
  }
`;

const AlertMessage = styled.label`
  color: #676767;
  font-weight: bold;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

// フォーム説明文の入力欄。実際の申込フォーム(MerchantApplicationForm)側の説明文表示
// (Wrapper max-width:800 − padding:24*2 − SubTitle padding:16*2 = テキスト幅720px)と
// 折返し位置を揃えるため、テキスト幅が720pxになるよう幅を固定する
// (border-box: 720 + padding:6*2 + border:1*2 = 734px)。狭い画面では溢れないよう max-width:100%。
const DescriptionTextArea = styled(TextArea)`
  width: 734px;
  max-width: 100%;
`;

// URL入力欄(リダイレクトURL/加盟店規約)はフォーム説明文(DescriptionTextArea)と
// 同じ734pxに幅を揃える。狭い画面では溢れないよう max-width:100%。
const UrlTextBox = styled(TextBox)`
  width: 734px;
  max-width: 100%;
`;

// 入力欄の下に出す補足文（プレースホルダに収まらない説明用）
const FieldNote = styled.p`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
    color: ${theme.semanticColors.text.secondary};
    margin: 4px 0 0 0;
  `}
`;

const InfoArea = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.gray[50]};
    border-radius: ${theme.borderRadius[8]};
    padding: ${theme.spacing[16]};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing[12]};
  `}
`;

const InfoLabel = styled(Label)`
  ${(props) => props.theme.typography.body2.regular}
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoItemList = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 28px;
  gap: 4px;
  > ${InfoLabel} {
    padding: 0;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
`;

// 削除は破壊的操作のため、白背景・赤枠・赤文字にする。
const DeleteButton = styled(Button)`
  border-color: #d75f48;
  color: #d75f48;
  &:hover {
    background: #fdf3f1;
  }
`;

const SelectOptionWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const LinkWrapper = styled.p`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LinkText = styled.a`
  ${({ theme }) => css`
    ${theme.typography.body2.regular}
    color: ${theme.semanticColors.text.primary} !important;
    text-decoration: underline !important;
  `}
`;

const FileInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UploadedFileInfo = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background-color: ${theme.colors.gray[50]};
    border-radius: ${theme.borderRadius[4]};
  `}
`;

const FileName = styled.span`
  ${({ theme }) => css`
    ${theme.typography.body2.regular}
    color: ${theme.semanticColors.text.primary};
  `}
`;

const UploadErrorMessage = styled.p`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
    color: ${theme.semanticColors.accent.red};
    margin: 4px 0 0 0;
  `}
`;

const TRADER_REQUIRED_ITEMS = [
  '屋号（ない場合は氏名）',
  '屋号（フリガナ）',
  '郵便番号',
  '納税地住所（開業届の記載住所）',
  '代表者氏名',
  '代表者氏名（フリガナ）',
  '代表者生年月日',
  '電話番号',
  'メールアドレス',
];

const CORPORATION_REQUIRED_ITEMS = [
  '法人番号',
  '法人名',
  '法人名（フリガナ）',
  '郵便番号',
  '本店所在地（登記住所）',
  '代表者氏名',
  '代表者氏名（フリガナ）',
  '代表者生年月日',
  '電話番号',
  'メールアドレス',
];

const BANK_TRANSFER_REQUIRED_ITEMS = [
  '金融機関名',
  '金融機関コード',
  '支店名',
  '支店コード',
  '口座種別',
  '口座番号',
  '口座名義（半角ｶﾀｶﾅ）',
];

const SHOP_INFO_REQUIRED_ITEMS = [
  '店舗名',
  '店舗名（フリガナ）',
  '店舗郵便番号',
  '店舗住所',
  '店舗電話番号',
  '店舗メールアドレス',
  '店舗担当者名',
  '店舗担当者名（フリガナ）',
  '店舗形態',
];

const STATUS_OPTIONS = [
  { label: '非公開', value: 'draft' },
  { label: '公開中', value: 'active' },
] as const;

const statusMap: Record<
  MerchantApplicationTemplateValues['status'],
  { color: ColorKey; gradient?: GradientKey }
> = {
  active: { color: 'green', gradient: '300' },
  draft: { color: 'white' },
  archived: { color: 'gray', gradient: '300' },
};

const CircleStatusIndicator = styled.div<{ $status: MerchantApplicationTemplateValues['status'] }>`
  ${({ theme: { colors, borderRadius }, $status }) => {
    const { color, gradient } = statusMap[$status];
    const backgroundColor =
      gradient && color !== 'white' && color !== 'black' ? colors[color][gradient] : colors[color];
    const borderColor = color === 'white' ? colors.gray['200'] : 'transparent';

    return css`
      width: 15px;
      height: 15px;
      border-radius: ${borderRadius.full};
      display: inline-block;
      background-color: ${backgroundColor};
      border: 1px solid ${borderColor};
    `;
  }}
`;

const formatOptionLabel = (option: (typeof STATUS_OPTIONS)[number]) => {
  return (
    <SelectOptionWrapper>
      <CircleStatusIndicator $status={option.value} />
      <div>{option.label}</div>
    </SelectOptionWrapper>
  );
};

const getBaseUrl = () => {
  const env = getFrontEnv();
  const baseUrl =
    env !== 'production' ? `https://admin-${env}.pokepay.jp` : `https://admin.pokepay.jp`;
  return baseUrl;
};

type TagGroupFieldOption = 'required' | 'optional' | 'hidden';

const EditMerchantApplicationTemplate = ({ mode, formValues, templateId, onConfirm }: Props) => {
  const history = useHistory();
  const [submitResult, setSubmitResult] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [termType, setTermType] = useState<'link' | 'pdf'>('link');
  // 発行体orgのタググループ（無効も含む）と、各グループの申込フォーム表示設定（必須/任意/非表示）
  const [tagGroups, setTagGroups] = useState<{ id: string; name: string; isActive: boolean }[]>(
    []
  );
  const [tagGroupOptions, setTagGroupOptions] = useState<Record<string, TagGroupFieldOption>>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const { organization: privateMoneyOrg } = useAppSelector(selectUserPrivateMoney);

  const issuedMoneys = user.issuedMoneys().filter((x) => x.type !== 'own');
  const privateMoneyOptions = issuedMoneys.map((money) => ({
    label: money.name,
    value: money.id,
  }));

  const defaultFormValues = useMemo<MerchantApplicationTemplateValues>(
    () => ({
      ...DEFAULT_FORM_VALUES,
      private_money_ids: issuedMoneys.map((money) => money.id),
    }),
    [issuedMoneys]
  );

  const formMethods = useForm<MerchantApplicationTemplateValues>({
    defaultValues: formValues ?? defaultFormValues,
  });

  const { reset, handleSubmit, control, getValues } = formMethods;

  useEffect(() => {
    reset(formValues ?? defaultFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, reset]);

  const fetchUserTagGroups = useCallback(async () => {
    if (!privateMoneyOrg?.code) return;

    try {
      // 元々紐づいていた（後から無効化された）グループも詳細で参照できるよう all を取得
      const groupsResponse = await getUserTagGroups(privateMoneyOrg.code, 'all');
      setTagGroups(
        groupsResponse.rows.map((group) => ({
          id: group.id,
          name: group.name_ja || group.name_en,
          isActive: group.is_active,
        }))
      );
    } catch (error) {
      console.error('Failed to fetch user tag groups:', error);
    }
  }, [privateMoneyOrg?.code]);

  useEffect(() => {
    fetchUserTagGroups();
  }, [fetchUserTagGroups]);

  // 各グループの表示設定を初期化。編集時は既存 user_tag_group_options を反映し、
  // 設定に無いグループ（および新規作成時）は「非表示」を既定とする。
  useEffect(() => {
    if (tagGroups.length === 0) return;
    const existing = formValues?.user_tag_group_options ?? [];
    const next: Record<string, TagGroupFieldOption> = {};
    tagGroups.forEach((group) => {
      const opt = existing.find((o) => o.user_tag_group_id === group.id);
      next[group.id] = opt ? opt.field_option : 'hidden';
    });
    setTagGroupOptions(next);
  }, [tagGroups, formValues]);

  // 非表示以外のグループを user_tag_group_options 形式に変換（payload は required/optional のみ）。
  // 無効化されたグループはサーバのバリデーション対象外（保存不可）のため除外する。
  const buildUserTagGroupOptions = (): UserTagGroupOption[] =>
    tagGroups
      .filter((g) => g.isActive && tagGroupOptions[g.id] && tagGroupOptions[g.id] !== 'hidden')
      .map((g) => ({
        user_tag_group_id: g.id,
        field_option: tagGroupOptions[g.id] as 'required' | 'optional',
      }));

  // 設定可能なのは有効グループのみ。元々紐づいていたが後から無効化されたグループは参照表示する。
  const activeTagGroups = tagGroups.filter((g) => g.isActive);
  const inactiveReferencedGroups = tagGroups.filter(
    (g) =>
      !g.isActive &&
      (formValues?.user_tag_group_options ?? []).some((o) => o.user_tag_group_id === g.id)
  );
  const fieldOptionLabel = (fo?: string): string => {
    if (fo === 'required') return '必須選択';
    if (fo === 'optional') return '任意選択';
    return '表示しない';
  };

  const openPreview = () => {
    const values = getValues();
    const params = new URLSearchParams();
    params.set('title', values.title);
    params.set('application_term_url', values.application_term_url);
    params.set('redirect_url', values.redirect_url);
    params.set('private_money_ids', values.private_money_ids.join(','));
    params.set('capital_field_option', values.capital_field_option);
    params.set('number_of_employees_field_option', values.number_of_employees_field_option);
    params.set('referral_source_field_option', values.referral_source_field_option);
    params.set('description', values.description);

    // Build user_tag_group_options for preview
    const userTagGroupOptions = buildUserTagGroupOptions();
    if (userTagGroupOptions.length > 0) {
      params.set('user_tag_group_options', JSON.stringify(userTagGroupOptions));
    }

    // プレビューはテンプレートの発行体orgを基準にする（service等が他発行体のテンプレートを
    // 編集する場合にログインユーザーorgだと誤ったURLになるため）。新規作成時は自org。
    const previewOrgCode = formValues?.issuer_organization_code ?? user.organization?.code;
    window.open(`/public/${previewOrgCode}/form/preview?${params.toString()}`, '_blank');
  };

  // バリデーション失敗時のフィードバック。エラーメッセージは画面上部の基本設定セクションに
  // 出る一方で送信ボタンは最下部にあり見えないため、上部固定のトーストで通知しつつ
  // 最初のエラー欄へスクロールする（前例: MerchantApplicationForm の onInvalid）。
  const onInvalid = useCallback(() => {
    setSubmitResult('error');
    setErrorMessage('入力内容に誤りがあります。エラー箇所をご確認ください。');
    // react-hook-form の errors 反映後の再レンダーを待ってからスクロールする
    setTimeout(() => {
      document
        .querySelector('.form-validation-error')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, []);

  const showConfirmModal = (values: MerchantApplicationTemplateValues) => {
    // バリデーション通過時は前回の失敗トーストを消す
    setSubmitResult(null);
    setErrorMessage(null);

    // Build user_tag_group_options based on selected options and existing groups
    const userTagGroupOptions = buildUserTagGroupOptions();

    const submitValues: MerchantApplicationTemplateValues = {
      ...values,
      user_tag_group_options: userTagGroupOptions,
    };

    dispatch(
      openAlertModal({
        body: (
          <AlertMessage>
            {mode === 'create' ? '登録をしてもよろしいですか？' : '更新をしてもよろしいですか？'}
          </AlertMessage>
        ),
        styles: {
          content: {
            padding: '64px 128px',
          },
        },
        confirmText: '実行',
        onConfirm: async () => {
          try {
            dispatch(showLoadingSpinner());

            let finalValues = { ...submitValues };

            if (mode === 'edit' && templateId) {
              // Edit mode: upload file first if selected, then update
              if (selectedFile) {
                const uploadResult = await uploadOrganizationApplicationTermFile(
                  templateId,
                  selectedFile
                );
                finalValues.application_term_url = uploadResult.url;
              }
              await onConfirm(finalValues);
            } else if (mode === 'create' && selectedFile) {
              // Create mode with PDF: create with dummy URL, upload file, then patch
              const DUMMY_TERM_URL = 'https://placeholder.pokepay.jp/term';
              finalValues.application_term_url = DUMMY_TERM_URL;

              const createResult = await onConfirm(finalValues);
              if (createResult?.id) {
                // Upload the PDF file
                const uploadResult = await uploadOrganizationApplicationTermFile(
                  createResult.id,
                  selectedFile
                );
                // Patch with the actual URL
                await updateOrganizationApplicationTemplate(createResult.id, {
                  application_term_url: uploadResult.url,
                });
              }
            } else {
              // Create mode without PDF or other cases
              await onConfirm(finalValues);
            }

            setSubmitResult('success');
            setErrorMessage(null);
            setSelectedFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          } catch (e: any) {
            setSubmitResult('error');
            setErrorMessage(e?.message ?? '予期しないエラーが発生しました');
          } finally {
            dispatch(hideLoadingSpinner());
          }
        },
      })
    );
  };

  const showRemoveConfirmModal = () => {
    if (mode !== 'edit' || !templateId) return;

    dispatch(
      openAlertModal({
        body: (
          <AlertMessage>
            この加盟店申込フォームを削除してもよろしいですか？
            <br />
            削除すると元に戻せません。
          </AlertMessage>
        ),
        styles: {
          content: {
            padding: '64px 128px',
          },
        },
        confirmText: '削除',
        onConfirm: async () => {
          try {
            dispatch(showLoadingSpinner());
            await updateOrganizationApplicationTemplate(templateId, { status: 'archived' });
            history.push('/organizations/application-form');
          } catch (e: any) {
            setSubmitResult('error');
            setErrorMessage(e?.message ?? '予期しないエラーが発生しました');
          } finally {
            dispatch(hideLoadingSpinner());
          }
        },
      })
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    if (file.type !== 'application/pdf') {
      setFileError('PDFファイルのみアップロードできます');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setFileError(null);
    setSelectedFile(file);
  };

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(showConfirmModal, onInvalid)}>
        <FormGrid>
          <Grid.Item size={2}>
            <Label>基本設定</Label>
          </Grid.Item>
          <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
            {mode === 'edit' && (
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  状態
                  <Required />
                </Label>

                <Controller
                  name="status"
                  control={control}
                  rules={{ required: '必須' }}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      fullWidth
                      options={STATUS_OPTIONS}
                      value={STATUS_OPTIONS.find((x) => x.value === value)}
                      onChange={(option) => option.value && onChange(option.value)}
                      formatOptionLabel={formatOptionLabel}
                    />
                  )}
                />
              </Grid.Item>
            )}
            <Grid.Item flexDirection="column" nextRow>
              <Label>
                加盟するマネー
                <Required />
              </Label>
              <Controller
                name="private_money_ids"
                control={control}
                rules={{ required: '加盟するマネーを選択してください' }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    fullWidth
                    isMulti
                    options={privateMoneyOptions}
                    value={privateMoneyOptions.filter((opt) => value?.includes(opt.value))}
                    onChange={(opts) => onChange(opts?.map((opt) => opt.value) ?? [])}
                    isDisabled={mode === 'edit'}
                  />
                )}
              />
              <FormErrorMessage name="private_money_ids" className="form-validation-error" />
            </Grid.Item>
            <Grid.Item flexDirection="column" nextRow>
              <Label>
                タイトル
                <Required />
              </Label>
              <Controller
                name="title"
                control={control}
                rules={{ required: 'タイトルを入力してください' }}
                render={({ field: { onChange, value } }) => (
                  <TextBox syncValue={value} onChange={onChange} fullWidth />
                )}
              />
              <FormErrorMessage name="title" className="form-validation-error" />
            </Grid.Item>
            <Grid.Item flexDirection="column" size={2} nextRow>
              <Label>フォーム説明文</Label>
              <Controller
                name="description"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DescriptionTextArea
                    value={value}
                    onChange={onChange}
                    rows={4}
                    fullWidth
                    placeholder="申込フォームのタイトル下に表示される説明文です。未入力の場合は既定の案内文が表示されます。"
                  />
                )}
              />
            </Grid.Item>
            <Grid.Item flexDirection="column" size={2} nextRow>
              <Label>申込完了後のリダイレクトURL</Label>
              <Controller
                name="redirect_url"
                control={control}
                rules={{
                  pattern: {
                    value: /^https?:\/\/.+/,
                    message: 'URLはhttp://またはhttps://で始まる必要があります',
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <UrlTextBox
                    syncValue={value}
                    onChange={onChange}
                    fullWidth
                    placeholder="https://"
                  />
                )}
              />
              <FormErrorMessage name="redirect_url" className="form-validation-error" />
              <FieldNote>未入力の場合は完了画面のままリダイレクトしません</FieldNote>
            </Grid.Item>
            <Grid.Item flexDirection="column" size={2} nextRow>
              <Label>
                加盟店規約
                <Required />
              </Label>
              {mode === 'edit' ? (
                // 編集時は設定済みURLをテキストボックスで直接編集できるようにする。
                // (PDF差し替えは未対応。サーバのPATCHは application_term_url を受け付ける)
                <>
                  <Controller
                    name="application_term_url"
                    control={control}
                    rules={{
                      required: '加盟店規約を設定してください',
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: 'URLはhttp://またはhttps://で始まる必要があります',
                      },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <UrlTextBox
                        syncValue={value}
                        onChange={onChange}
                        fullWidth
                        placeholder="https://"
                      />
                    )}
                  />
                  <FormErrorMessage name="application_term_url" className="form-validation-error" />
                  <LinkText href={formValues.application_term_url} target="_blank">
                    設定中の規約を開く
                  </LinkText>
                </>
              ) : (
                <>
                  <RadioGroup>
                    <RadioLabel>
                      <Radio
                        name="termType"
                        checked={termType === 'link'}
                        setChecked={() => setTermType('link')}
                      />
                      リンク
                    </RadioLabel>
                    <RadioLabel>
                      <Radio
                        name="termType"
                        checked={termType === 'pdf'}
                        setChecked={() => setTermType('pdf')}
                      />
                      PDFファイル
                    </RadioLabel>
                  </RadioGroup>
                  {termType === 'link' && (
                    <>
                      <Controller
                        name="application_term_url"
                        control={control}
                        rules={{
                          required: '加盟店規約を設定してください',
                          pattern: {
                            value: /^https?:\/\/.+/,
                            message: 'URLはhttp://またはhttps://で始まる必要があります',
                          },
                        }}
                        render={({ field: { onChange, value } }) => (
                          <UrlTextBox
                            syncValue={value}
                            onChange={onChange}
                            fullWidth
                            placeholder="https://"
                          />
                        )}
                      />
                      <FormErrorMessage name="application_term_url" className="form-validation-error" />
                    </>
                  )}
                  {termType === 'pdf' && (
                    <FileInputWrapper>
                      <Input
                        ref={fileInputRef}
                        fullWidth
                        type="file"
                        accept="application/pdf"
                        onChange={handleFileChange}
                      />
                      {selectedFile && (
                        <UploadedFileInfo>
                          <FileName>選択中: {selectedFile.name}</FileName>
                        </UploadedFileInfo>
                      )}
                      {fileError && <UploadErrorMessage>{fileError}</UploadErrorMessage>}
                    </FileInputWrapper>
                  )}
                </>
              )}
            </Grid.Item>
            {mode === 'edit' && (
              <Grid.Item flexDirection="column" size={2} nextRow>
                <Label>
                  発行URL <Required />
                </Label>
                <LinkWrapper>
                  <LinkText
                    href={`${getBaseUrl()}/public/${formValues.issuer_organization_code}/form/${
                      formValues.id
                    }`}
                    target="_blank"
                  >{`${getBaseUrl()}/public/${formValues.issuer_organization_code}/form/${
                    formValues.id
                  }`}</LinkText>
                  <CopyToClipboard
                    text={`${getBaseUrl()}/public/${formValues.issuer_organization_code}/form/${
                      formValues.id
                    }`}
                  />
                </LinkWrapper>
              </Grid.Item>
            )}
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>個人事業主</Label>
          </Grid.Item>
          <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
            <Grid.Item flexDirection="column">
              <InfoArea>
                <InfoLabel>
                  <img src={InfoIcon} alt="info-icon" /> 以下必須項目のため設定不要
                </InfoLabel>
                <InfoItemList>
                  {TRADER_REQUIRED_ITEMS.map((item) => (
                    <InfoLabel key={item}>{item}</InfoLabel>
                  ))}
                </InfoItemList>
              </InfoArea>
            </Grid.Item>
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>法人</Label>
          </Grid.Item>
          <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
            <Grid.Item flexDirection="column">
              <InfoArea>
                <InfoLabel>
                  <img src={InfoIcon} alt="info-icon" /> 以下必須項目のため設定不要
                </InfoLabel>
                <InfoItemList>
                  {CORPORATION_REQUIRED_ITEMS.map((item) => (
                    <InfoLabel key={item}>{item}</InfoLabel>
                  ))}
                </InfoItemList>
              </InfoArea>
            </Grid.Item>
            <Grid.Item flexDirection="column" nextRow>
              <Label>資本金（円）</Label>
              <Controller
                name="capital_field_option"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <RadioGroup>
                    <RadioLabel>
                      <Radio
                        name="capital_field_option"
                        checked={value === 'required'}
                        setChecked={() => onChange('required')}
                      />
                      必須入力
                    </RadioLabel>
                    <RadioLabel>
                      <Radio
                        name="capital_field_option"
                        checked={value === 'optional'}
                        setChecked={() => onChange('optional')}
                      />
                      任意入力
                    </RadioLabel>
                    <RadioLabel>
                      <Radio
                        name="capital_field_option"
                        checked={value === 'hidden'}
                        setChecked={() => onChange('hidden')}
                      />
                      表示しない
                    </RadioLabel>
                  </RadioGroup>
                )}
              />
            </Grid.Item>
            <Grid.Item flexDirection="column" nextRow>
              <Label>従業員数</Label>
              <Controller
                name="number_of_employees_field_option"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <RadioGroup>
                    <RadioLabel>
                      <Radio
                        name="number_of_employees_field_option"
                        checked={value === 'required'}
                        setChecked={() => onChange('required')}
                      />
                      必須入力
                    </RadioLabel>
                    <RadioLabel>
                      <Radio
                        name="number_of_employees_field_option"
                        checked={value === 'optional'}
                        setChecked={() => onChange('optional')}
                      />
                      任意入力
                    </RadioLabel>
                    <RadioLabel>
                      <Radio
                        name="number_of_employees_field_option"
                        checked={value === 'hidden'}
                        setChecked={() => onChange('hidden')}
                      />
                      表示しない
                    </RadioLabel>
                  </RadioGroup>
                )}
              />
            </Grid.Item>
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>紹介元</Label>
          </Grid.Item>
          <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
            <Grid.Item flexDirection="column">
              <Label>紹介元（申込み経路）</Label>
              <Controller
                name="referral_source_field_option"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <RadioGroup>
                    <RadioLabel>
                      <Radio
                        name="referral_source_field_option"
                        checked={value === 'required'}
                        setChecked={() => onChange('required')}
                      />
                      必須入力
                    </RadioLabel>
                    <RadioLabel>
                      <Radio
                        name="referral_source_field_option"
                        checked={value === 'optional'}
                        setChecked={() => onChange('optional')}
                      />
                      任意入力
                    </RadioLabel>
                    <RadioLabel>
                      <Radio
                        name="referral_source_field_option"
                        checked={value === 'hidden'}
                        setChecked={() => onChange('hidden')}
                      />
                      表示しない
                    </RadioLabel>
                  </RadioGroup>
                )}
              />
            </Grid.Item>
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>振り込み先情報</Label>
          </Grid.Item>
          <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
            <Grid.Item flexDirection="column">
              <InfoArea>
                <InfoLabel>
                  <img src={InfoIcon} alt="info-icon" /> 以下必須項目のため設定不要
                </InfoLabel>
                <InfoItemList>
                  {BANK_TRANSFER_REQUIRED_ITEMS.map((item) => (
                    <InfoLabel key={item}>{item}</InfoLabel>
                  ))}
                </InfoItemList>
              </InfoArea>
            </Grid.Item>
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>店舗情報</Label>
          </Grid.Item>
          <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
            <Grid.Item flexDirection="column">
              <InfoArea>
                <InfoLabel>
                  <img src={InfoIcon} alt="info-icon" /> 以下必須項目のため設定不要
                </InfoLabel>
                <InfoItemList>
                  {SHOP_INFO_REQUIRED_ITEMS.map((item) => (
                    <InfoLabel key={item}>{item}</InfoLabel>
                  ))}
                </InfoItemList>
              </InfoArea>
            </Grid.Item>
            <Grid.Item flexDirection="column" nextRow>
              <Label>
                店舗ラベル
                <Tooltip
                  id="shop-label-setting"
                  text="申込フォームで申請者に選択させる店舗ラベルを設定します"
                  place="top"
                />
              </Label>
            </Grid.Item>
            {activeTagGroups.map((group) => (
              <Grid.Item key={group.id} flexDirection="column" nextRow>
                <Label>{group.name}</Label>
                <RadioGroup>
                  <RadioLabel>
                    <Radio
                      name={`tag_group_option_${group.id}`}
                      checked={tagGroupOptions[group.id] === 'required'}
                      setChecked={() =>
                        setTagGroupOptions((prev) => ({ ...prev, [group.id]: 'required' }))
                      }
                    />
                    必須選択
                  </RadioLabel>
                  <RadioLabel>
                    <Radio
                      name={`tag_group_option_${group.id}`}
                      checked={tagGroupOptions[group.id] === 'optional'}
                      setChecked={() =>
                        setTagGroupOptions((prev) => ({ ...prev, [group.id]: 'optional' }))
                      }
                    />
                    任意選択
                  </RadioLabel>
                  <RadioLabel>
                    <Radio
                      name={`tag_group_option_${group.id}`}
                      checked={
                        !tagGroupOptions[group.id] || tagGroupOptions[group.id] === 'hidden'
                      }
                      setChecked={() =>
                        setTagGroupOptions((prev) => ({ ...prev, [group.id]: 'hidden' }))
                      }
                    />
                    表示しない
                  </RadioLabel>
                </RadioGroup>
              </Grid.Item>
            ))}
            {inactiveReferencedGroups.map((group) => {
              const fo = (formValues?.user_tag_group_options ?? []).find(
                (o) => o.user_tag_group_id === group.id
              )?.field_option;
              return (
                <Grid.Item key={group.id} flexDirection="column" nextRow>
                  <Label style={{ color: '#999' }}>{group.name}（無効）</Label>
                  <InfoLabel style={{ color: '#999' }}>
                    このグループは無効化されています（設定: {fieldOptionLabel(fo)}）。申込フォームには表示されません。
                  </InfoLabel>
                </Grid.Item>
              );
            })}
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={12}>
            <ButtonRow>
              <Button type="submit">{mode === 'create' ? '発行' : '更新'}</Button>
              <Button type="button" variant="muted-outline" onClick={openPreview}>
                プレビュー
              </Button>
              {mode === 'edit' && (
                <DeleteButton type="button" variant="outline" onClick={showRemoveConfirmModal}>
                  削除
                </DeleteButton>
              )}
            </ButtonRow>
          </Grid.Item>
        </FormGrid>
      </Form>

      <NotificationToast
        isOpen={submitResult !== null}
        variant={submitResult === 'error' ? 'error' : 'success'}
        message={
          submitResult === 'error'
            ? errorMessage
            : mode === 'create'
            ? '加盟店申込フォームの発行に成功しました'
            : '加盟店申込フォームの更新に成功しました'
        }
        onClose={() => {
          setSubmitResult(null);
          setErrorMessage(null);
        }}
      />
    </FormProvider>
  );
};

export default EditMerchantApplicationTemplate;
